import {getFunctions, httpsCallable} from "firebase/functions";
import {app} from "../firebase";
import {JANAKPURI_HOTEL_ID} from "../services/janakpuriBookingService";
import {createBookingPaymentClient} from "../services/bookingPaymentService";
// ===============================
// MainPage.js
// Hotel Loyalty + Spin Points
// 4 Digit Check-In Code
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { sendTelegram } from "../telegram";
import "./MainPage.css";

const RECEIVER_UPI_ID = "rk8282091@okicici";
const RECEIVER_NAME = "Hotel Dev Palace";
const PAY_AMOUNT = 1;
const PAY_TIMER_SECONDS = 150;

// false = payment button hidden
const ENABLE_PAYMENT = false;

function MainPage() {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [retryPaymentId, setRetryPaymentId] = useState("");
  const [userBookings, setUserBookings] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showPayPopup, setShowPayPopup] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(
    PAY_TIMER_SECONDS
  );

  // ===============================
  // HELPERS
  // ===============================

  const getHotelNameById = (hotelId) => {
    const hotel = hotels.find(
      (item) => item.id === hotelId
    );

    return hotel?.name || hotelId || "Hotel";
  };

  const getBookingFinalPrice = (booking) => {
    return Number(
      booking?.finalPrice ??
        booking?.price ??
        0
    );
  };

  const isHotelFreeBooking = (booking) => {
    return (
      booking?.isHotelFree === true ||
      booking?.paymentType ===
        "free_hotel_loyalty"
    );
  };

  const isBookingCheckedIn = (booking) => {
    return (
      booking?.checkedIn === true ||
      booking?.status === "checked_in"
    );
  };

  // Manager portal button sirf authorized staff ko dikhega.
  // Firestore managerAccess=true preferred hai.
  // Mohit Sir ka existing user account fallback whitelist me bhi allowed hai,
  // taaki field add na hone par bhi manager button turant visible ho.
  const canOpenManagerDashboard = useMemo(() => {
    const roles = [
      userData?.role,
      userData?.userType,
      userData?.staffRole,
    ].map((value) =>
      String(value || "")
        .trim()
        .toLowerCase()
    );

    const phone = String(
      userData?.phoneNumber ||
        userData?.phone ||
        ""
    ).replace(/\D/g, "");

    const managerPhoneWhitelist = [
      "8810663507",
    ];

    return (
      userData?.managerAccess === true ||
      userData?.isManager === true ||
      roles.includes("manager") ||
      roles.includes("admin") ||
      managerPhoneWhitelist.includes(phone)
    );
  }, [userData]);

  const openManagerDashboard = () => {
    const savedHotelId =
      localStorage.getItem("managerHotelId") ||
      sessionStorage.getItem("managerHotelId");

    const savedLoggedIn =
      localStorage.getItem("managerLoggedIn") === "true" ||
      sessionStorage.getItem("managerLoggedIn") === "true";

    if (savedLoggedIn && savedHotelId) {
      navigate("/manager-dashboard");
      return;
    }

    navigate("/manager-login");
  };

  const loyaltyEntries = useMemo(() => {
    const pointsObject =
      userData?.hotelBookingPoints || {};

    return Object.entries(pointsObject)
      .map(([hotelId, points]) => ({
        hotelId,
        hotelName: getHotelNameById(hotelId),
        points: Number(points || 0),
      }))
      .filter((item) => item.points > 0)
      .sort((first, second) => {
        return second.points - first.points;
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, hotels]);

  // ===============================
  // LOAD HOTELS
  // ===============================

  const loadHotels = async () => {
    try {
      const snapshot = await getDocs(
        collection(db, "hotels")
      );

      const hotelList = snapshot.docs.map(
        (hotelDocument) => ({
          id: hotelDocument.id,
          ...hotelDocument.data(),
        })
      );

      setHotels(hotelList);
    } catch (error) {
      console.error(
        "Hotels load error:",
        error
      );
    }
  };

  // ===============================
  // LOAD BOOKINGS
  // ===============================

  const loadBookings = async (userId) => {
    try {
      const bookingsQuery = query(
        collection(db, "bookings"),
        where("userId", "==", userId)
      );

      const snapshot = await getDocs(
        bookingsQuery
      );

      const bookingList = snapshot.docs.map(
        (bookingDocument) => ({
          id: bookingDocument.id,
          ...bookingDocument.data(),
        })
      );

      bookingList.sort((first, second) => {
        const firstTime =
          first?.createdAt?.toMillis
            ? first.createdAt.toMillis()
            : 0;

        const secondTime =
          second?.createdAt?.toMillis
            ? second.createdAt.toMillis()
            : 0;

        return secondTime - firstTime;
      });

      setUserBookings(bookingList);
    } catch (error) {
      console.error(
        "Bookings load error:",
        error
      );

      throw error;
    }
  };

  // ===============================
  // AUTH + PAGE LOAD
  // ===============================

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        try {
          if (!firebaseUser) {
            setUserData(null);

            navigate("/", {
              replace: true,
            });

            return;
          }

          await loadHotels();

          const userRef = doc(
            db,
            "users",
            firebaseUser.uid
          );

          let userSnapshot =
            await getDoc(userRef);
          const pendingSignup = sessionStorage.getItem(`pendingSignup_${firebaseUser.uid}`);
          if (!userSnapshot.exists() && pendingSignup) {
            await httpsCallable(getFunctions(app, "asia-south1"), "completeCustomerSignup")(JSON.parse(pendingSignup));
            userSnapshot = await getDoc(userRef);
          }
          if (userSnapshot.exists()) sessionStorage.removeItem(`pendingSignup_${firebaseUser.uid}`);

          if (!userSnapshot.exists()) {
            setUserData(null);

            alert(
              "User profile nahi mili. Please login again."
            );

            await signOut(auth);

            navigate("/", {
              replace: true,
            });

            return;
          }

          setUserData({
            uid: firebaseUser.uid,
            ...userSnapshot.data(),
          });

          await loadBookings(
            firebaseUser.uid
          );
        } catch (error) {
          console.error(
            "MainPage load error:",
            error
          );

          alert(
            "MainPage error: " +
              (error?.message || error)
          );
        } finally {
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!userData?.uid) return undefined;
    const unsubscribeUser = onSnapshot(doc(db, 'users', userData.uid), snapshot => {
      if (snapshot.exists()) setUserData({uid: snapshot.id, ...snapshot.data()});
    });
    const unsubscribeBookings = onSnapshot(query(collection(db, 'bookings'), where('userId', '==', userData.uid)), snapshot => {
      setUserBookings(snapshot.docs.map(item => ({id: item.id, ...item.data()})).sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    });
    return () => {unsubscribeUser(); unsubscribeBookings();};
  }, [userData?.uid]);

  const retryBookingPayment = async booking => {
    if (booking.hotelId === JANAKPURI_HOTEL_ID || booking.slotBookingVersion === 1) return;
    if (retryPaymentId) return;
    setRetryPaymentId(booking.id);
    try {
      const pay = createBookingPaymentClient(userData, {name: booking.hotel}, {title: booking.planTitle});
      const result = await pay({bookingId: booking.id, checkInCode: booking.checkInCode});
      alert(result.paid ? 'Payment verified ✅' : 'Payment pending hai; isi booking ko retry kar sakte hain.');
    } catch (error) {alert(error.message);}
    finally {setRetryPaymentId('');}
  };

  // ===============================
  // LOGOUT
  // ===============================

  const handleLogout = async () => {
    const confirmed = window.confirm(
      "Logout karna hai?"
    );

    if (!confirmed) return;

    try {
      await signOut(auth);

      navigate("/", {
        replace: true,
      });
    } catch (error) {
      console.error(
        "Logout error:",
        error
      );

      alert(
        "Logout error: " +
          (error?.message || error)
      );
    }
  };

  // ===============================
  // SHARE REFERRAL
  // ===============================

  const handleShare = () => {
    if (!userData) return;

    const shareText =
      `Hi, my name is ${
        userData.name || ""
      }.\n\n` +
      `Book your hotel through Hotel Dev Palace:\n` +
      `https://bookinghoteldevpalace.com/\n\n` +
      `Use my referral number:\n` +
      `${userData.phoneNumber || ""}\n\n` +
      `After your successful check-in, I will earn 1 Spin Point.`;

    if (navigator.share) {
      navigator
        .share({
          title:
            "Hotel Dev Palace Booking",
          text: shareText,
          url: "https://bookinghoteldevpalace.com/",
        })
        .catch((error) => {
          console.error(
            "Share error:",
            error
          );
        });
    } else {
      const whatsappUrl =
        "https://wa.me/?text=" +
        encodeURIComponent(shareText);

      window.open(
        whatsappUrl,
        "_blank"
      );
    }
  };

  // ===============================
  // PAYMENT HELPERS
  // ===============================

  const buildPaymentNote = (booking) => {
    const planText =
      booking?.planTitle ||
      (booking?.planHours
        ? `${booking.planHours} Hours`
        : "-");

    return (
      `Booking Confirm Payment | ` +
      `Name: ${userData?.name || "-"} | ` +
      `Phone: ${
        userData?.phoneNumber || "-"
      } | ` +
      `Hotel: ${
        booking?.hotel || "-"
      } | ` +
      `Date: ${
        booking?.date || "-"
      } | ` +
      `Time: ${
        booking?.time || "-"
      } | ` +
      `Plan: ${planText} | ` +
      `BookingId: ${
        booking?.id || "-"
      }`
    );
  };

  const buildUpiUrl = (booking) => {
    const paymentNote =
      buildPaymentNote(booking);

    return (
      "upi://pay?pa=" +
      encodeURIComponent(
        RECEIVER_UPI_ID
      ) +
      "&pn=" +
      encodeURIComponent(
        RECEIVER_NAME
      ) +
      "&am=" +
      encodeURIComponent(
        String(PAY_AMOUNT)
      ) +
      "&cu=INR" +
      "&tn=" +
      encodeURIComponent(paymentNote)
    );
  };

  const openUpiApp = (booking) => {
    if (!ENABLE_PAYMENT) return;

    if (
      !RECEIVER_UPI_ID ||
      !RECEIVER_UPI_ID.includes("@")
    ) {
      alert(
        "UPI ID properly set nahi hai."
      );

      return;
    }

    setSelectedBooking(booking);
    setSecondsLeft(
      PAY_TIMER_SECONDS
    );
    setShowPayPopup(true);

    const upiUrl =
      buildUpiUrl(booking);

    window.location.href = upiUrl;
  };

  const markPaidRequested = async () => {
    if (!selectedBooking?.id) return;

    try {
      await updateDoc(
        doc(
          db,
          "bookings",
          selectedBooking.id
        ),
        {
          status: "paid_requested",
          paidAt: serverTimestamp(),
          paidAmount: PAY_AMOUNT,
          paidUpiId: RECEIVER_UPI_ID,
        }
      );

      await sendTelegram(
        `💰 <b>PAYMENT SENT</b>\n` +
          `🆔 <b>${selectedBooking.id}</b>\n` +
          `👤 ${
            userData?.name || "-"
          }\n` +
          `📞 ${
            userData?.phoneNumber || "-"
          }\n` +
          `🏨 ${
            selectedBooking.hotel || "-"
          }\n` +
          `🕒 ${
            selectedBooking.date || "-"
          } ${
            selectedBooking.time || "-"
          }\n` +
          `✅ Amount: ₹${PAY_AMOUNT}\n` +
          `UPI: ${RECEIVER_UPI_ID}`
      );

      alert(
        "Payment marked successfully ✅"
      );

      setShowPayPopup(false);
      setSelectedBooking(null);

      await loadBookings(
        userData.uid
      );
    } catch (error) {
      console.error(
        "Payment status error:",
        error
      );

      alert(
        "Status update error: " +
          (error?.message || error)
      );
    }
  };

  const upiUrlForPopup = useMemo(() => {
    if (!selectedBooking) return "";

    return buildUpiUrl(
      selectedBooking
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBooking, userData]);

  // ===============================
  // PAYMENT TIMER
  // ===============================

  useEffect(() => {
    if (!showPayPopup) return;

    const timer = setInterval(() => {
      setSecondsLeft((currentSeconds) => {
        if (currentSeconds <= 1) {
          return 0;
        }

        return currentSeconds - 1;
      });
    }, 1000);

    return () =>
      clearInterval(timer);
  }, [showPayPopup]);

  useEffect(() => {
    if (
      showPayPopup &&
      secondsLeft === 0
    ) {
      setShowPayPopup(false);
      setSelectedBooking(null);
    }
  }, [
    showPayPopup,
    secondsLeft,
  ]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(
      seconds / 60
    );

    const remainingSeconds =
      seconds % 60;

    return `${minutes}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  // ===============================
  // LOADING
  // ===============================

  if (loading) {
    return (
      <div style={{ padding: 25 }}>
        Loading...
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={{ padding: 25 }}>
        No user found. Please login
        again.
      </div>
    );
  }

  // ===============================
  // UI
  // ===============================

  return (
    <div
      className="main-bg"
      style={{
        backgroundImage:
          "url('/images/bg.jpg')",
      }}
    >
      <div
        className="card"
        style={{
          position: "relative",
        }}
      >
        {/* Logout */}

        <button
          onClick={handleLogout}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
            padding: "8px 14px",
            border: "none",
            borderRadius: 10,
            background: "#e53935",
            color: "#ffffff",
            fontSize: 14,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow:
              "0 6px 16px rgba(229,57,53,0.25)",
          }}
        >
          Logout
        </button>

        {/* Logo */}

        <div className="auth-logo">
          <img
            src="/images/el-logo.png"
            alt="Hotel Dev Palace"
          />
        </div>

        <h2>
          Welcome, {userData.name} 👋
        </h2>

        {/* User Reward Details */}

        <div className="reward-lines">
          <p>
            <b>Your Phone:</b>{" "}
            {userData.phoneNumber}
          </p>

          <p>
            <b>Spin Points:</b>{" "}
            <span
              style={{
                color: "#7c3aed",
                fontWeight: 900,
              }}
            >
              🎡{" "}
              {Number(
                userData.spinPoints || 0
              )}
            </span>
          </p>

          <p>
            <b>
              1 successful referral
              check-in = 1 Spin Point
            </b>
          </p>

          <p>
            <b>
              1 booking = Maximum 1 Spin
            </b>
          </p>

          <p>
            <b>
              10 completed stays in the
              same hotel = Next stay free
            </b>
          </p>
        </div>

        {/* Hotel Loyalty */}

        <div
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 16,
            background:
              "rgba(255,255,255,0.10)",
            border:
              "1px solid rgba(255,255,255,0.18)",
            textAlign: "left",
          }}
        >
          <h3
            style={{
              marginTop: 0,
              marginBottom: 12,
            }}
          >
            🏨 Hotel Loyalty Progress
          </h3>

          {loyaltyEntries.length ===
          0 ? (
            <div
              style={{
                fontSize: 14,
                opacity: 0.9,
              }}
            >
              Abhi koi completed hotel
              stay nahi hai.
            </div>
          ) : (
            loyaltyEntries.map(
              (loyaltyItem) => {
                const progressPoints =
                  Math.min(
                    loyaltyItem.points,
                    10
                  );

                const progressPercentage =
                  Math.min(
                    100,
                    (progressPoints / 10) *
                      100
                  );

                return (
                  <div
                    key={
                      loyaltyItem.hotelId
                    }
                    style={{
                      marginBottom: 14,
                      padding: 12,
                      borderRadius: 12,
                      background:
                        "rgba(255,255,255,0.10)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 800,
                        }}
                      >
                        {
                          loyaltyItem.hotelName
                        }
                      </div>

                      <div
                        style={{
                          fontWeight: 900,
                          color:
                            loyaltyItem.points >=
                            10
                              ? "#4ade80"
                              : "#ffffff",
                        }}
                      >
                        {
                          loyaltyItem.points
                        }
                        /10
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 8,
                        width: "100%",
                        height: 9,
                        overflow: "hidden",
                        borderRadius: 999,
                        background:
                          "rgba(255,255,255,0.20)",
                      }}
                    >
                      <div
                        style={{
                          width: `${progressPercentage}%`,
                          height: "100%",
                          borderRadius: 999,
                          background:
                            loyaltyItem.points >=
                            10
                              ? "#22c55e"
                              : "#8b5cf6",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        marginTop: 7,
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {loyaltyItem.points >=
                      10
                        ? "🎁 Free Stay Available"
                        : `${
                            10 -
                            loyaltyItem.points
                          } more completed stay(s) needed`}
                    </div>
                  </div>
                );
              }
            )
          )}
        </div>

        {/* Main Buttons */}

        <div className="btn-group">
          {canOpenManagerDashboard && (
            <button type="button" onClick={openManagerDashboard} className="btn"
              style={{ background: "#0f766e", color: "#fff", fontWeight: 900 }}>
              🏨 Manager Dashboard
            </button>
          )}

          <button
            onClick={() =>
              navigate("/booking")
            }
            className="btn primary"
          >
            Book a Room
          </button>

          <button
            onClick={handleShare}
            className="btn success"
          >
            Share & Earn Spin 🔗
          </button>
        </div>

        {/* Booking List */}

        <div className="bookings-wrap">
          <h3>Your Bookings</h3>

          {userBookings.length ===
            0 && (
            <div className="no-bookings">
              No active bookings.
            </div>
          )}

          {userBookings.map(
            (booking) => {
              const isFree =
                isHotelFreeBooking(
                  booking
                );

              const isCheckedIn =
                isBookingCheckedIn(
                  booking
                );

              const finalBookingPrice =
                getBookingFinalPrice(
                  booking
                );

              return (
                <div
                  key={booking.id}
                  className="booking-box"
                >
                  <div className="booking-hotel">
                    {booking.hotel || "-"}
                  </div>

                  <p>
                    <b>Date:</b>{" "}
                    {booking.date || "-"}
                  </p>

                  <p>
                    <b>Time:</b>{" "}
                    {booking.time || "-"}
                  </p>

                  <p>
                    <b>Plan:</b>{" "}
                    {booking.planTitle ||
                      "-"}{" "}
                    {booking.planHours
                      ? `• ${booking.planHours} hrs`
                      : ""}
                  </p>

                  {/* Price */}

                  <p>
                    <b>Price:</b>{" "}
                    {isFree ? (
                      <span
                        style={{
                          color: "#0a7",
                          fontWeight: 900,
                        }}
                      >
                        FREE 🎁
                      </span>
                    ) : booking.spinUsed ? (
                      <>
                        <span
                          style={{
                            marginRight: 8,
                            textDecoration:
                              "line-through",
                            opacity: 0.7,
                          }}
                        >
                          ₹
                          {booking.originalPrice ||
                            0}
                        </span>

                        <span
                          style={{
                            color: "#0a7",
                            fontWeight: 900,
                          }}
                        >
                          ₹
                          {
                            finalBookingPrice
                          }
                        </span>
                      </>
                    ) : (
                      <span
                        style={{
                          fontWeight: 800,
                        }}
                      >
                        ₹
                        {
                          finalBookingPrice
                        }
                      </span>
                    )}
                  </p>

                  {/* Check-In Code */}

                  {!isCheckedIn &&
                    booking.checkInCode && booking.status !== 'awaiting_confirmation' && (
                      <div
                        style={{
                          marginTop: 10,
                          padding:
                            "12px 14px",
                          borderRadius: 13,
                          background:
                            "#fff7ed",
                          border:
                            "1px solid #fed7aa",
                          color: "#9a3412",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          🔐 CHECK-IN CODE
                        </div>

                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 26,
                            fontWeight: 900,
                            letterSpacing: 6,
                          }}
                        >
                          {
                            booking.checkInCode
                          }
                        </div>

                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          Hotel reception par
                          ye code manager ko
                          batayein.
                        </div>
                      </div>
                    )}

                  {isCheckedIn &&
                    booking.checkInCodeVerified && (
                      <div
                        style={{
                          marginTop: 9,
                          padding: 10,
                          borderRadius: 11,
                          background:
                            "#ecfdf5",
                          border:
                            "1px solid #bbf7d0",
                          color: "#15803d",
                          fontSize: 13,
                          fontWeight: 900,
                        }}
                      >
                        🔐 Check-In Code
                        Verified ✅
                      </div>
                    )}

                  {/* Spin Offer */}

                  {booking.spinUsed && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: 10,
                        borderRadius: 12,
                        background:
                          "#f5f3ff",
                        border:
                          "1px solid #ddd6fe",
                        color: "#4c1d95",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 900,
                        }}
                      >
                        🎡 Spin Offer
                        Applied
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        Offer:{" "}
                        {booking.spinDiscountLabel ||
                          "-"}
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          fontSize: 13,
                        }}
                      >
                        Original: ₹
                        {booking.originalPrice ||
                          0}{" "}
                        • Discount: ₹
                        {booking.discountAmount ||
                          0}{" "}
                        • Final: ₹
                        {
                          finalBookingPrice
                        }
                      </div>
                    </div>
                  )}

                  {booking.hotelId !== JANAKPURI_HOTEL_ID && booking.slotBookingVersion !== 1 && booking.paid !== true && !['cancelled', 'expired', 'checked_in'].includes(booking.status) && Number(booking.finalPrice ?? booking.price) > 0 && (
                    <button disabled={Boolean(retryPaymentId)} onClick={() => retryBookingPayment(booking)} style={{marginTop: 12, padding: '10px 16px'}}>
                      {retryPaymentId === booking.id ? 'Payment loading...' : 'Complete Payment'}
                    </button>
                  )}

                  {/* Free Stay */}

                  {isFree && (
                    <div className="free-stay-box">
                      <div className="free-stay-title">
                        🎁 Free Stay — Hotel
                        Loyalty
                      </div>

                      <div className="free-stay-points">
                        Same Hotel Points:{" "}
                        {booking.hotelPointsBefore ??
                          10}{" "}
                        → -10 ={" "}
                        {booking.hotelPointsAfter ??
                          0}
                      </div>
                    </div>
                  )}

                  {/* Booking Point */}

                  {booking.bookingPointGiven && (
                    <div
                      style={{
                        marginTop: 8,
                        color: "#2563eb",
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      ⭐ This hotel booking
                      point added: +1
                    </div>
                  )}

                  {/* Status */}

                  <p className="status-line">
                    <b>Status:</b>{" "}

                    {booking.slotBookingVersion === 1 ? (
                      <span>{booking.status==='awaiting_confirmation'?'You are in queue, please wait for confirmation':String(booking.status || '').replaceAll('_', ' ')}</span>
                    ) : isCheckedIn ? (
                      <span className="status-done">
                        Checked In ✅
                      </span>
                    ) : isFree ? (
                      <span className="status-free">
                        Free Stay Confirmed 🎁
                      </span>
                    ) : booking.status ===
                      "pending" ? (
                      <span className="status-pending">
                        Pending ⏳
                      </span>
                    ) : booking.status ===
                      "paid_requested" ? (
                      <span className="status-paid">
                        Payment Sent
                        (Waiting Verify) ✅
                      </span>
                    ) : booking.status ===
                      "done" ? (
                      <span className="status-done">
                        Confirmed ✅
                      </span>
                    ) : (
                      <span>
                        {booking.status ||
                          "-"}
                      </span>
                    )}
                  </p>

                  {booking.slotBookingVersion === 1 && <p><b>Pay at Hotel</b> · Room {booking.room}<br />Advance received: ₹{Number(booking.advanceAmount||0)} · Remaining at check-in: ₹{Math.max(0,Number(booking.finalPrice||0)-Math.max(Number(booking.advanceAmount||0),Number(booking.paidAmount||0)))}<br /><a href="/janakpuri">View Janakpuri booking details</a></p>}
                  {/* Payment */}

                  {ENABLE_PAYMENT &&
                    !isFree &&
                    booking.status ===
                      "pending" && (
                      <button
                        onClick={() =>
                          openUpiApp(
                            booking
                          )
                        }
                        className="pay-btn-main"
                      >
                        Pay Advance to
                        Confirm ₹
                        {PAY_AMOUNT}
                      </button>
                    )}
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* Payment Popup */}

      {ENABLE_PAYMENT &&
        showPayPopup &&
        selectedBooking && (
          <div
            className="pay-popup-overlay"
            onClick={() =>
              setShowPayPopup(false)
            }
          >
            <div
              className="pay-popup-box"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <div className="pay-popup-top">
                <h3 className="pay-popup-title">
                  Advance Payment ₹
                  {PAY_AMOUNT}
                </h3>

                <button
                  onClick={() =>
                    setShowPayPopup(
                      false
                    )
                  }
                  className="pay-close-btn"
                >
                  ✕
                </button>
              </div>

              <div className="pay-mini-text">
                <div>
                  <b>To:</b>{" "}
                  {RECEIVER_NAME}
                </div>

                <div>
                  <b>UPI:</b>{" "}
                  {RECEIVER_UPI_ID}
                </div>

                <div
                  style={{
                    marginTop: 6,
                  }}
                >
                  <b>Booking:</b>{" "}
                  {
                    selectedBooking.hotel
                  }
                </div>
              </div>

              <div className="pay-timer-box">
                <span>Timer</span>

                <span
                  style={{
                    color:
                      secondsLeft <= 10
                        ? "#d00"
                        : "#111",
                  }}
                >
                  {formatTime(
                    secondsLeft
                  )}
                </span>
              </div>

              <div className="pay-qr-wrap">
                <div className="pay-qr-inner">
                  <QRCodeCanvas
                    value={
                      upiUrlForPopup
                    }
                    size={190}
                    includeMargin
                  />
                </div>
              </div>

              <p className="pay-note">
                Payment complete hone ke
                baad “I Have Paid” dabao.
              </p>

              <button
                onClick={
                  markPaidRequested
                }
                className="pay-btn green"
              >
                ✅ I Have Paid
              </button>

              <button
                onClick={() => {
                  setShowPayPopup(false);
                  setSelectedBooking(null);
                }}
                className="pay-btn gray"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
    </div>
  );
}

export default MainPage;